import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  BarChart3, Calendar, Clock, Loader2, TrendingUp, Users, FileText, ArrowLeft, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area, LineChart, Line, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import CsvExportButton from '@/components/CsvExportButton';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = { open: '#3b82f6', in_progress: '#f59e0b', resolved: '#22c55e', closed: '#9ca3af' };
const PRIORITY_COLORS = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
const CHART_COLORS = ['#6366f1', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#f97316'];

const toLocalYYYYMMDD = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d)) return '';
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
};

const BarRow = ({ label, value, max, color, pct }) => {
  const p = pct !== undefined ? pct : (max > 0 ? Math.round((value / max) * 100) : 0);
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="text-sm text-gray-600 w-28 shrink-0 truncate">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${p}%`, backgroundColor: color }} />
      </div>
      <span className="text-sm font-bold text-gray-800 w-8 text-right">{value}</span>
      <span className="text-xs text-gray-400 w-8">{p}%</span>
    </div>
  );
};

const KPICard = ({ title, value, sub, icon: Icon, color }) => (
  <Card className={`border-l-4`} style={{ borderLeftColor: color }}>
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
      {Icon && (
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      )}
    </CardContent>
  </Card>
);

const Reports = ({ user }) => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('monthly');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [reportData, setReportData] = useState(null);
  const [allTickets, setAllTickets] = useState([]);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    fetchReport();
    fetchAllTickets();
    fetchCompanies();
  }, []); // eslint-disable-line

  useEffect(() => { if (period !== 'custom') fetchReport(); }, [period]); // eslint-disable-line

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`, getAuthHeaders());
      const compMap = {};
      res.data.forEach(c => { compMap[c.id] = c.name; });
      setCompanies(compMap);
    } catch { }
  };

  const fetchAllTickets = async () => {
    try {
      const res = await axios.get(`${API}/tickets`, getAuthHeaders());
      setAllTickets(res.data);
    } catch { }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/reports?period=${period}`;

      const now = new Date();
      let start = new Date();
      let end = new Date();
      let sendCustom = false;

      if (period === 'daily') {
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        sendCustom = true;
      } else if (period === 'weekly') {
        start.setDate(now.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        sendCustom = true;
      } else if (period === 'monthly') {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        sendCustom = true;
      } else if (period === 'quarterly') {
        const q = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), q * 3, 1);
        sendCustom = true;
      } else if (period === 'yearly') {
        start = new Date(now.getFullYear(), 0, 1);
        sendCustom = true;
      } else if (period === 'custom' && customDateRange.start && customDateRange.end) {
        start = new Date(customDateRange.start + 'T00:00:00');
        end = new Date(customDateRange.end + 'T23:59:59');
        sendCustom = true;
      }

      if (sendCustom) {
        url += `&start_date=${encodeURIComponent(start.toISOString())}&end_date=${encodeURIComponent(end.toISOString())}`;
      }

      const res = await axios.get(url, getAuthHeaders());
      setReportData(res.data);
    } catch { toast.error('Failed to load report data'); }
    finally { setLoading(false); }
  };

  // ── Derived ──────────────────────────────────────────────────────────────

  const tickets = (() => {
    if (!reportData) return [];
    const { start, end } = reportData.date_range || {};
    const startLocal = toLocalYYYYMMDD(start);
    const endLocal = toLocalYYYYMMDD(end);
    if (!startLocal || !endLocal) return allTickets;
    return allTickets.filter(t => {
      const d = toLocalYYYYMMDD(t.due_date);
      if (!d) return false;
      return d >= startLocal && d <= endLocal;
    });
  })();

  const byStatus = reportData?.tickets_by_status || {};
  const byPriority = reportData?.tickets_by_priority || {};
  const total = reportData?.summary?.total_tickets || 0;

  // Tickets per day chart
  const dailyData = (() => {
    const map = {};
    tickets.forEach(t => {
      const d = toLocalYYYYMMDD(t.due_date);
      if (!d) return;
      if (!map[d]) map[d] = { date: d, open: 0, in_progress: 0, resolved: 0, closed: 0 };
      map[d][t.status] = (map[d][t.status] || 0) + 1;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date)).map(d => ({
      ...d,
      date: new Date(d.date + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }));
  })();

  // Hours per day
  const hoursDaily = (() => {
    const map = {};
    tickets.forEach(t => {
      if (!t.total_hours_spent) return;
      const d = toLocalYYYYMMDD(t.due_date);
      if (!d) return;
      map[d] = (map[d] || 0) + t.total_hours_spent;
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([date, hours]) => ({
      date: new Date(date + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      hours: parseFloat(hours.toFixed(1)),
    }));
  })();

  // Top clients (by company, not by internal assignees)
  const topClients = (() => {
    const map = {};
    tickets.forEach(t => {
      // Find the name via company_id; skip if it doesn't have an ID or if we can't find a matching name
      const name = companies[t.company_id];
      if (!name) return; 
      
      if (!map[name]) map[name] = { name, tickets: 0, hours: 0 };
      map[name].tickets++;
      map[name].hours += t.total_hours_spent || 0;
    });
    return Object.values(map).sort((a, b) => b.tickets - a.tickets).slice(0, 8).map(c => ({
      ...c,
      hours: parseFloat(c.hours.toFixed(1)),
    }));
  })();

  // Problem type breakdown
  const byType = (() => {
    const map = {};
    tickets.forEach(t => {
      const types = (t.problem_type || 'other').split(',').map(s => s.trim()).filter(Boolean);
      types.forEach(rawType => {
        let k = rawType.replace(/_/g, ' ');
        k = k.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        if (!map[k]) map[k] = { type: k, count: 0, hours: 0 };
        map[k].count++;
        // Distribute hours evenly among types, or just add to all? Adding to all for simplicity since it's an overview
        map[k].hours += t.total_hours_spent || 0;
      });
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 8).map(d => ({
      ...d,
      hours: parseFloat(d.hours.toFixed(1)),
    }));
  })();

  // Radar data for priority vs status
  const radarData = [
    { metric: 'Critical Open', value: tickets.filter(t => t.priority === 'critical' && t.status !== 'resolved' && t.status !== 'closed').length },
    { metric: 'High Open', value: tickets.filter(t => t.priority === 'high' && t.status === 'open').length },
    { metric: 'Overdue', value: tickets.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'resolved' && t.status !== 'closed').length },
    { metric: 'No Hours', value: tickets.filter(t => !t.total_hours_spent).length },
    { metric: 'Resolved', value: byStatus.resolved || 0 },
    { metric: 'In Progress', value: byStatus.in_progress || 0 },
  ];

  const pieStatusData = [
    { name: 'Open', value: byStatus.open || 0, color: STATUS_COLORS.open },
    { name: 'In Progress', value: byStatus.in_progress || 0, color: STATUS_COLORS.in_progress },
    { name: 'Resolved', value: byStatus.resolved || 0, color: STATUS_COLORS.resolved },
    { name: 'Closed', value: byStatus.closed || 0, color: STATUS_COLORS.closed },
  ].filter(d => d.value > 0);

  const piePriorityData = [
    { name: 'Critical', value: byPriority.critical || 0, color: PRIORITY_COLORS.critical },
    { name: 'High', value: byPriority.high || 0, color: PRIORITY_COLORS.high },
    { name: 'Medium', value: byPriority.medium || 0, color: PRIORITY_COLORS.medium },
    { name: 'Low', value: byPriority.low || 0, color: PRIORITY_COLORS.low },
  ].filter(d => d.value > 0);

  const resolvedRate = total > 0 ? Math.round((((byStatus.resolved || 0) + (byStatus.closed || 0)) / total) * 100) : 0;
  const totalHours = reportData?.summary?.total_hours_spent || 0;
  const avgResolution = reportData?.summary?.avg_resolution_time_hours || 0;
  const overdueCount = tickets.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'resolved' && t.status !== 'closed').length;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/admin')} className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Reports & Analytics</h1>
                  <p className="text-xs text-gray-500">Deep insights into your support operations</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Period Selector */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-8 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Time Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="mt-1.5 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Today</SelectItem>
                <SelectItem value="weekly">Last 7 Days</SelectItem>
                <SelectItem value="monthly">This Month</SelectItem>
                <SelectItem value="quarterly">This Quarter</SelectItem>
                <SelectItem value="yearly">Year to Date</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period === 'custom' && (
            <>
              <div className="min-w-[150px]">
                <Label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Start</Label>
                <Input type="date" value={customDateRange.start}
                  onChange={e => setCustomDateRange(r => ({ ...r, start: e.target.value }))}
                  className="mt-1.5 h-9" />
              </div>
              <div className="min-w-[150px]">
                <Label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">End</Label>
                <Input type="date" value={customDateRange.end}
                  onChange={e => setCustomDateRange(r => ({ ...r, end: e.target.value }))}
                  className="mt-1.5 h-9" />
              </div>
              <Button onClick={() => { if (customDateRange.start && customDateRange.end) fetchReport(); else toast.error('Select both dates'); }}
                className="bg-black hover:bg-gray-800 h-9">
                Apply
              </Button>
            </>
          )}
          <CsvExportButton
            filename={`support-report-${period}`}
            rows={tickets}
            columns={[
              { label: 'Ticket ID', value: 'id' },
              { label: 'Title', value: 'title' },
              { label: 'Company', value: row => companies[row.company_id] || row.company_name || '' },
              { label: 'Status', value: 'status' },
              { label: 'Priority', value: 'priority' },
              { label: 'Problem Type', value: 'problem_type' },
              { label: 'Due Date', value: 'due_date' },
              { label: 'Hours', value: 'total_hours_spent' },
              { label: 'Created By', value: 'created_by_email' },
            ]}
          />
          {reportData && (
            <span className="text-xs text-gray-400 self-end pb-1 ml-auto">
              {formatDate(reportData.date_range?.start)} → {formatDate(reportData.date_range?.end)}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : !reportData ? (
          <div className="text-center py-16 text-gray-400">Select a period above to load the report.</div>
        ) : (
          <>
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <KPICard title="Total Tickets" value={total} sub="in period" icon={FileText} color="#6366f1" />
              <KPICard title="Hours Logged" value={`${totalHours}h`} sub="total work time" icon={Clock} color="#f59e0b" />
              <KPICard title="Avg Resolution" value={`${avgResolution}h`} sub="mean time to resolve" icon={TrendingUp} color="#22c55e" />
              <KPICard title="Resolution Rate" value={`${resolvedRate}%`} sub={`${(byStatus.resolved || 0) + (byStatus.closed || 0)} of ${total} resolved`} icon={AlertCircle} color="#3b82f6" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <KPICard title="Open" value={byStatus.open || 0} icon={Clock} color="#3b82f6" />
              <KPICard title="In Progress" value={byStatus.in_progress || 0} icon={Loader2} color="#f59e0b" />
              <KPICard title="Critical" value={byPriority.critical || 0} icon={AlertCircle} color="#ef4444" />
              <KPICard title="Overdue" value={overdueCount} sub="past due date" icon={AlertCircle} color="#ec4899" />
            </div>

            {/* Daily volume stacked area */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">Daily Ticket Volume by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {dailyData.length === 0 ? (
                  <div className="h-48 flex items-center justify-center text-gray-400">No data in range</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={dailyData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        {Object.entries(STATUS_COLORS).map(([k, c]) => (
                          <linearGradient key={k} id={`grad_${k}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={c} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={c} stopOpacity={0.05} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      {Object.entries(STATUS_COLORS).map(([k, c]) => (
                        <Area key={k} type="monotone" dataKey={k} name={k.replace('_', ' ')} stroke={c} fill={`url(#grad_${k})`} strokeWidth={2} stackId="1" />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* 3-col: Status pie + Priority pie + Radar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardHeader><CardTitle className="text-base">By Status</CardTitle></CardHeader>
                <CardContent>
                  {pieStatusData.length === 0 ? <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No data</div> : (
                    <>
                      <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                          <Pie data={pieStatusData} dataKey="value" cx="50%" cy="50%" innerRadius={36} outerRadius={60} paddingAngle={3}>
                            {pieStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="mt-2 space-y-1">
                        {pieStatusData.map(d => <BarRow key={d.name} label={d.name} value={d.value} max={total} color={d.color} />)}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">By Priority</CardTitle></CardHeader>
                <CardContent>
                  {piePriorityData.length === 0 ? <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No data</div> : (
                    <>
                      <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                          <Pie data={piePriorityData} dataKey="value" cx="50%" cy="50%" outerRadius={60} paddingAngle={3}>
                            {piePriorityData.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="mt-2 space-y-1">
                        {piePriorityData.map(d => <BarRow key={d.name} label={d.name} value={d.value} max={total} color={d.color} />)}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Health Radar</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                      <PolarRadiusAxis tick={{ fontSize: 9 }} />
                      <Radar dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.25} />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Hours logged per day */}
            {hoursDaily.length > 0 && (
              <Card className="mb-6">
                <CardHeader><CardTitle className="text-base">Hours Logged Per Day</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={hoursDaily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={v => [`${v}h`, 'Hours']} />
                      <Bar dataKey="hours" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* 2-col: Top clients + Problem type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Top Companies by Tickets</CardTitle></CardHeader>
                <CardContent>
                  {topClients.length === 0 ? <p className="text-gray-400 text-sm text-center py-6">No data</p> : (
                    <div className="space-y-2">
                      {topClients.map((c, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                          <div className="w-7 h-7 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                            <p className="text-xs text-gray-400">{c.hours}h logged</p>
                          </div>
                          <span className="text-sm font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">{c.tickets}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Tickets by Problem Type</CardTitle></CardHeader>
                <CardContent>
                  {byType.length === 0 ? <p className="text-gray-400 text-sm text-center py-6">No data</p> : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={byType} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="type" tick={{ fontSize: 10 }} width={105} />
                        <Tooltip />
                        <Bar dataKey="count" name="Tickets" fill="#6366f1" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="hours" name="Hours" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Top users table */}
            {reportData.top_users?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Top Users by Activity</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tickets</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-48">Volume</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.top_users.map((u, i) => (
                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2.5 px-3 text-gray-400 font-medium">{i + 1}</td>
                            <td className="py-2.5 px-3 font-medium text-gray-800">{u.email}</td>
                            <td className="py-2.5 px-3 text-right font-bold text-gray-900">{u.tickets}</td>
                            <td className="py-2.5 px-3">
                              <div className="w-full bg-gray-100 rounded-full h-1.5">
                                <div className="bg-indigo-500 h-1.5 rounded-full"
                                  style={{ width: `${reportData.top_users[0]?.tickets > 0 ? (u.tickets / reportData.top_users[0].tickets) * 100 : 0}%` }} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Reports;
